/**
 * statsVideo — the stats charts building up, one per slide, as a video.
 *
 * Each slide is socialExport's own chart (drawChart, so the video matches the
 * PNGs in every format and style): 1.15s of build, 1.45s of hold, a short fade
 * from the ground at each cut. Encoding is videoEncode's — MP4 through WebCodecs
 * where the browser has it, a real-time recording elsewhere.
 */

import { encodeCanvasVideo } from '@/lib/videoEncode';
import {
  drawChart,
  loadShareFonts,
  SIZES,
  type ChartSpec,
  type SocialFormat,
  type SocialStyle,
} from '@/lib/socialExport';

const BUILD = 1.15;
const HOLD = 1.45;
const FADE = 0.22;

export interface StatsVideoOpts {
  format?: SocialFormat;
  style?: Exclude<SocialStyle, 'poster'>;
  fps?: number;
  header?: boolean;
  onProgress?: (fraction: number) => void;
}

/** Seconds the stats video runs for `n` charts. */
export function statsVideoSeconds(n: number): number {
  return n * (BUILD + HOLD);
}

/** Render the animated stats video and trigger the download. Resolves to the file's extension. */
export async function recordStatsVideo(specs: ChartSpec[], opts: StatsVideoOpts = {}): Promise<'mp4' | 'webm'> {
  const { format = 'post', style = 'light', fps = 30, header = false, onProgress } = opts;
  const slides = specs.filter((s) => s.items && s.items.length > 0);
  if (!slides.length) throw new Error('No stats to animate');
  await loadShareFonts();

  const [W, H] = SIZES[format];
  const work = document.createElement('canvas');
  work.width = W;
  work.height = H;
  const ctx = work.getContext('2d')!;
  const buildF = Math.round(BUILD * fps);
  const slideF = Math.round((BUILD + HOLD) * fps);
  const fadeF = Math.round(FADE * fps);
  const total = slides.length * slideF;
  const dark = style !== 'light';
  const ground = style === 'black' ? '#000000' : dark ? '#1a1a1a' : '#f5f5f5';

  const drawFrame = (n: number) => {
    const idx = Math.min(slides.length - 1, Math.floor(n / slideF));
    const local = n - idx * slideF;
    drawChart(ctx, slides[idx], format, style, { header, progress: Math.min(1, local / buildF) });
    if (idx > 0 && local < fadeF) {
      ctx.save();
      ctx.globalAlpha = 1 - local / fadeF;
      ctx.fillStyle = ground;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
  };

  return encodeCanvasVideo(work, { fps, total, drawFrame, name: `prompt-brush-stats-${format}-${style}`, onProgress });
}
